import { memo, useState, useEffect, useRef } from 'react';
import { EditIcon } from '@page/icons';

import { Button, IconButton, Input, Popover, PopoverContent, PopoverTrigger } from '../../designSystem';
import { cn } from '../../lib/styles';

interface EditableInlineValueProps {
  value: string | null | undefined;
  editable?: boolean;
  tooltip?: string;
  className?: string;
  children?: React.ReactNode;
  placeholder?: string;
  /** Enter edit mode by clicking the displayed value instead of the edit icon. */
  editOnClick?: boolean;
  /** Save when the input loses focus (click outside) or Enter is pressed, without a Save button. */
  autoSave?: boolean;
  formatter?: (value: string) => string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: (newValue: string) => void;
}

const EditableInlineValueBase = ({
  value,
  editable = true,
  tooltip = 'Edit',
  className,
  children,
  placeholder,
  editOnClick = false,
  autoSave = false,
  formatter,
  onChange,
  onSave,
}: EditableInlineValueProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newValue, setNewValue] = useState(value ? value.toString() : '');
  const inputRef = useRef<HTMLInputElement>(null);
  const hasSavedRef = useRef(false);

  const handleEdit = () => {
    hasSavedRef.current = false;
    setIsEditing(true);
    setNewValue(value ? value.toString() : '');
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      });
    }
  }, [isEditing]);

  const handleSave = () => {
    if (hasSavedRef.current) return;
    hasSavedRef.current = true;
    setIsEditing(false);
    onSave(newValue);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewValue(event.target.value);
    onChange?.(event);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ' ') {
      event.stopPropagation();
    }

    if (event.key === 'Enter') {
      handleSave();
    }
  };

  const renderValue = (value: string | null | undefined) => {
    if (!value) return '--';

    return formatter ? formatter(value) : value;
  };

  const renderContent = () => {
    if (!isEditing)
      return (
        <div
          data-slot="content"
          data-editing={isEditing}
          className={cn('flex items-center gap-1 group/number', editable && editOnClick && 'w-fit cursor-pointer')}
          onClick={editable && editOnClick ? handleEdit : undefined}
        >
          {children ?? (
            <div data-slot="value" className="font-body-sm text-text-primary">
              {renderValue(value)}
            </div>
          )}

          {editable ? (
            <IconButton
              size="sm"
              onClick={handleEdit}
              tooltip={tooltip}
              className="group-hover/number:opacity-100 group-hover/number:pointer-events-auto opacity-0 pointer-events-none transition-all"
            >
              <EditIcon className="size-5 min-w-5 fill-icon-primary" />
            </IconButton>
          ) : null}
        </div>
      );

    if (autoSave)
      return (
        <div data-slot="content" data-editing={isEditing} className="flex items-center gap-2 relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={newValue}
            onChange={handleChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleSave}
            onClickCapture={(event) => event.stopPropagation()}
          />
        </div>
      );

    return (
      <div data-slot="content" data-editing={isEditing} className="flex items-center gap-2 relative">
        <Popover open={isEditing} onOpenChange={setIsEditing}>
          <PopoverTrigger onClickCapture={(event) => event.stopPropagation()}>
            <Input ref={inputRef} type="text" value={newValue} onChange={handleChange} onKeyDown={handleInputKeyDown} />
          </PopoverTrigger>

          <PopoverContent naked className="flex gap-1 p-1 w-fit" align="start">
            <Button variant="secondary" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>

            <Button onClick={handleSave}>Save</Button>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  return <div className={className}>{renderContent()}</div>;
};

export const EditableInlineValue = memo(EditableInlineValueBase);
